import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { registrationApi } from '../../api/registrationApi';
import { DropdownItem, PublicRegistrationContext, SubmitRegistration } from '../../types';

const steps = ['Mobile Number', 'Verify OTP', 'Registration'];

const EmployeeRegistration: React.FC = () => {
  const [searchParams] = useSearchParams();
  const tenantId = useMemo(() => searchParams.get('tenantId')?.trim() ?? '', [searchParams]);

  const [context, setContext] = useState<PublicRegistrationContext | null>(null);
  const [loadingContext, setLoadingContext] = useState(true);
  const [contextError, setContextError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [otpId, setOtpId] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [otpExpiresAt, setOtpExpiresAt] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [form, setForm] = useState<SubmitRegistration>({
    tenantId: tenantId,
    fullName: '',
    mobileNumber: '',
    verificationToken: '',
    gender: '',
    dateOfBirth: '',
    maritalStatus: '',
    email: '',
  });

  useEffect(() => {
    let cancelled = false;

    const loadContext = async () => {
      if (!tenantId) {
        setContextError('Registration link is invalid. Tenant information is missing.');
        setLoadingContext(false);
        return;
      }

      try {
        const response = await registrationApi.getPublicContext(tenantId);
        if (cancelled) {
          return;
        }

        setContext(response);
        setForm((previous) => ({ ...previous, tenantId: response.tenantId }));
      } catch (error: any) {
        if (!cancelled) {
          setContextError(error?.response?.data?.message || 'Unable to load registration details.');
        }
      } finally {
        if (!cancelled) {
          setLoadingContext(false);
        }
      }
    };

    loadContext();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const handleSendOtp = async () => {
    setSubmitting(true);
    setMessage(null);

    try {
      const response = await registrationApi.sendRegistrationOtp({ tenantId, mobileNumber });
      setOtpId(response.otpId);
      setOtpExpiresAt(response.expiresAt);
      setMobileNumber(response.mobileNumber);
      setForm((previous) => ({ ...previous, mobileNumber: response.mobileNumber }));
      setActiveStep(1);
      setMessage({ type: 'success', text: 'OTP sent successfully.' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.response?.data?.message || 'Failed to send OTP.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    setSubmitting(true);
    setMessage(null);

    try {
      const response = await registrationApi.verifyRegistrationOtp({
        tenantId,
        otpId,
        mobileNumber,
        otp,
      });
      setVerificationToken(response.verificationToken);
      setForm((previous) => ({ ...previous, verificationToken: response.verificationToken }));
      setActiveStep(2);
      setMessage({ type: 'success', text: 'Mobile number verified.' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.response?.data?.message || 'Failed to verify OTP.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitRegistration = async () => {
    if (!form.fullName.trim() || !form.gender || !form.dateOfBirth || !form.maritalStatus || !form.email?.trim()) {
      setMessage({ type: 'error', text: 'Please complete all registration fields.' });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      await registrationApi.submit({
        ...form,
        tenantId,
        mobileNumber,
        verificationToken,
      });
      setRegistrationComplete(true);
      setMessage({ type: 'success', text: 'Registration submitted successfully. Please wait for HR approval.' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.response?.data?.message || 'Failed to submit registration.' });
    } finally {
      setSubmitting(false);
    }
  };

  const renderSelectOptions = (items: DropdownItem[]) =>
    items.map((item) => (
      <MenuItem key={String(item.id)} value={String(item.id)}>
        {item.name}
      </MenuItem>
    ));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 5,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Paper elevation={10} sx={{ width: '100%', maxWidth: 720, p: { xs: 3, sm: 4 }, borderRadius: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight="bold" color="primary.main">
            Employee Registration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {context?.organizationName ? `Complete your onboarding for ${context.organizationName}.` : 'Complete your onboarding request.'}
          </Typography>
        </Box>

        {loadingContext ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : contextError ? (
          <Box>
            <Alert severity="error" sx={{ mb: 3 }}>{contextError}</Alert>
            <Button component={RouterLink} to="/login" variant="contained">Back to Login</Button>
          </Box>
        ) : (
          <>
            <Stepper activeStep={registrationComplete ? steps.length : activeStep} alternativeLabel sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {message && <Alert severity={message.type} sx={{ mb: 3 }}>{message.text}</Alert>}

            {registrationComplete ? (
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                  Registration Submitted
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  HR will review your details. After approval, you can sign in with your employee code and password shared by your organization.
                </Typography>
                <Button component={RouterLink} to="/login" variant="contained">Go to Login</Button>
              </Box>
            ) : (
              <>
                {activeStep === 0 && (
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                      Verify Your Mobile Number
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Enter your mobile number to receive an OTP on WhatsApp.
                    </Typography>
                    <TextField
                      fullWidth
                      label="Mobile Number"
                      value={mobileNumber}
                      onChange={(event) => setMobileNumber(event.target.value.replace(/\D/g, '').slice(0, 10))}
                      inputProps={{ maxLength: 10 }}
                    />
                    <Button
                      variant="contained"
                      sx={{ mt: 3 }}
                      disabled={submitting || mobileNumber.length !== 10}
                      onClick={handleSendOtp}
                    >
                      {submitting ? 'Sending OTP...' : 'Send OTP'}
                    </Button>
                  </Box>
                )}

                {activeStep === 1 && (
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                      Enter OTP
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      OTP sent to {mobileNumber}. {otpExpiresAt ? `Valid until ${new Date(otpExpiresAt).toLocaleTimeString()}.` : ''}
                    </Typography>
                    <TextField
                      fullWidth
                      label="OTP"
                      value={otp}
                      onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                      inputProps={{ maxLength: 6 }}
                    />
                    <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                      <Button variant="outlined" onClick={() => setActiveStep(0)} disabled={submitting}>Back</Button>
                      <Button variant="contained" onClick={handleVerifyOtp} disabled={submitting || otp.length !== 6}>
                        {submitting ? 'Verifying...' : 'Verify OTP'}
                      </Button>
                      <Button variant="text" onClick={handleSendOtp} disabled={submitting}>Resend OTP</Button>
                    </Box>
                  </Box>
                )}

                {activeStep === 2 && (
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                      Complete Registration Form
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Fill in your details and submit them for approval.
                    </Typography>

                    <input type="hidden" name="tenantId" value={tenantId} />
                    <input type="hidden" name="verificationToken" value={verificationToken} />

                    <TextField
                      fullWidth
                      label="Full Name"
                      value={form.fullName}
                      onChange={(event) => setForm((previous) => ({ ...previous, fullName: event.target.value }))}
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={form.email || ''}
                      onChange={(event) => setForm((previous) => ({ ...previous, email: event.target.value }))}
                      sx={{ mb: 2 }}
                    />

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel id="registration-gender-label">Gender</InputLabel>
                      <Select
                        labelId="registration-gender-label"
                        label="Gender"
                        value={form.gender || ''}
                        onChange={(event) => setForm((previous) => ({ ...previous, gender: event.target.value }))}
                      >
                        {renderSelectOptions(context?.genders ?? [])}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Date of Birth"
                      type="date"
                      value={form.dateOfBirth || ''}
                      onChange={(event) => setForm((previous) => ({ ...previous, dateOfBirth: event.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ mb: 2 }}
                    />

                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel id="registration-marital-status-label">Marital Status</InputLabel>
                      <Select
                        labelId="registration-marital-status-label"
                        label="Marital Status"
                        value={form.maritalStatus || ''}
                        onChange={(event) => setForm((previous) => ({ ...previous, maritalStatus: event.target.value }))}
                      >
                        {renderSelectOptions(context?.maritalStatuses ?? [])}
                      </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button variant="outlined" onClick={() => setActiveStep(1)} disabled={submitting}>Back</Button>
                      <Button variant="contained" onClick={handleSubmitRegistration} disabled={submitting}>
                        {submitting ? 'Submitting...' : 'Submit Registration'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            )}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Link component={RouterLink} to="/login" underline="hover">Back to login</Link>
              <Typography variant="caption" color="text.secondary">
                Tenant onboarding route secured by mobile verification.
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default EmployeeRegistration;